import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Surface OAuth callback errors from ?error=... query param
  useEffect(() => {
    const urlError = searchParams.get('error');
    if (urlError) setError(decodeURIComponent(urlError));
  }, [searchParams]);

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (document.querySelector(`script[src*="recaptcha/api.js"]`)) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  const executeRecaptcha = async (action) => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return null;
    try {
      // Race grecaptcha against a 3s timeout so a slow/blocked script
      // never blocks the login form.
      return await Promise.race([
        new Promise((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(RECAPTCHA_SITE_KEY, { action })
              .then(resolve)
              .catch(() => resolve(null));
          });
        }),
        new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);
    } catch (e) {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        const token = await executeRecaptcha('login');
        result = await login(email, password, token);
      } else {
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        result = await register(email, password, name);
      }

      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    } catch (e) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0A2216] to-[#0D0D0D] border-2 border-[#D4AF37] mx-auto flex items-center justify-center mb-4">
            <div className="w-8 h-10 bg-[#D4AF37]" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
          </div>
          <h1 className="font-['Bebas_Neue'] text-3xl tracking-wider">IAMHETHATIS</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Young Black Man Inc.</p>
        </div>

        {/* Form */}
        <div className="bg-[#1A1A1A] border border-white/10 p-8">
          <h2 className="font-['Bebas_Neue'] text-2xl text-center mb-8">
            {isLogin ? 'SIGN IN' : 'CREATE ACCOUNT'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="relative">
                <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full Name"
                  className="w-full pl-12 pr-4 py-4 bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none text-white placeholder-[#737373] transition-colors"
                  data-testid="register-name"
                />
              </div>
            )}

            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full pl-12 pr-4 py-4 bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none text-white placeholder-[#737373] transition-colors"
                data-testid="login-email"
                required
              />
            </div>

            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full pl-12 pr-12 py-4 bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none text-white placeholder-[#737373] transition-colors"
                data-testid="login-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {error && (
              <p className="text-red-500 text-sm text-center" data-testid="auth-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-submit"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>
            {isLogin && RECAPTCHA_SITE_KEY && (
              <p className="text-xs text-[#737373] text-center -mt-3" data-testid="recaptcha-notice">
                Protected by reCAPTCHA &middot;{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Privacy</a>
                {' '}&middot;{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Terms</a>
              </p>
            )}

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#1A1A1A] text-[#737373]">OR</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full py-4 bg-white text-black font-medium flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors"
              data-testid="google-signin"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.8055 10.2292C19.8055 9.55167 19.7501 8.86833 19.6311 8.19917H10.2V12.0492H15.6014C15.373 13.2908 14.6569 14.3892 13.6146 15.0875V17.5858H16.8251C18.7175 15.8449 19.8055 13.2717 19.8055 10.2292Z" fill="#4285F4"/>
                <path d="M10.2 20C12.9564 20 15.2682 19.1042 16.8287 17.5858L13.6182 15.0875C12.7354 15.6967 11.5923 16.0425 10.2036 16.0425C7.54042 16.0425 5.28953 14.2833 4.51812 11.9167H1.21484V14.4917C2.81734 17.6592 6.30042 20 10.2 20Z" fill="#34A853"/>
                <path d="M4.51474 11.9167C4.06906 10.675 4.06906 9.33001 4.51474 8.08834V5.51334H1.21474C-0.252265 8.43501 -0.252265 11.9708 1.21474 14.8925L4.51474 11.9167Z" fill="#FBBC04"/>
                <path d="M10.2 3.95833C11.6741 3.9325 13.096 4.47417 14.1675 5.48084L17.0233 2.625C15.1816 0.90167 12.7393 -0.0341634 10.2 0.000836593C6.30042 0.000836593 2.81734 2.34167 1.21484 5.51333L4.51484 8.08833C5.28297 5.71667 7.53714 3.95833 10.2 3.95833Z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#A3A3A3] text-sm">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-[#D4AF37] ml-2 hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-[#737373] mt-6">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
